import { MessageType } from 'ts-components/Chat';
import { QuickResponseData } from 'ts-components/QuickResponses';
import {
  MessageStreamYield,
  ButtonSpaceData,
  BackendButton,
  HistoryMessage,
  PreviewItem,
} from './types';
import { SPACE_NAMES, DEFAULT_PLACEHOLDER } from './constants';

// TODO remove this with KZN-18583
export const normalizeHistoryMessage = (
  msg: HistoryMessage
): HistoryMessage => {
  if (msg.space !== undefined) return msg;
  if (msg.assistant_message !== undefined) {
    return {
      ...msg,
      space: SPACE_NAMES.ASSISTANT_MESSAGE,
      content: msg.assistant_message,
    };
  }
  if (msg.user_message !== undefined) {
    return {
      ...msg,
      space: SPACE_NAMES.USER_MESSAGE,
      content: msg.user_message,
    };
  }
  return msg;
};

export const parseButtonSpace = (
  content: string | BackendButton[] | ButtonSpaceData
): QuickResponseData => {
  try {
    const data = typeof content === 'string' ? JSON.parse(content) : content;

    if (Array.isArray(data)) {
      const buttons = data as BackendButton[];
      if (buttons.length === 0) return { options: [], selectType: 'action' };

      const types = buttons.map((b) => b.type);
      const uniqueTypes = new Set(types);

      let selectType: QuickResponseData['selectType'] = 'action';
      if (uniqueTypes.size === 1) {
        const singleType = types[0];
        selectType = singleType === 'url' ? 'action' : singleType;
      } else if (!types.includes('url')) {
        selectType = 'single_select';
      }

      return {
        options: buttons.map((button) => ({
          label: button.text,
          value: button.chat_message || button.text,
          url: button.url,
        })),
        selectType,
      };
    }

    const buttonData = data as ButtonSpaceData;
    return {
      options: buttonData.options.map((opt) => ({
        label: opt.label,
        value: opt.value,
        url: opt.url,
      })),
      selectType:
        buttonData.actionType === 'url' ? 'action' : buttonData.actionType,
    };
  } catch {
    return { options: [], selectType: 'action' };
  }
};

const buildStatusSummaryMessage = (
  msg: HistoryMessage,
  isLastTurn: boolean,
  isComplete: boolean,
  statusDetailsByTurn: Map<number, string>
): MessageType => {
  const isStreaming = isLastTurn && !isComplete;
  const details = statusDetailsByTurn.get(msg.turn_count);

  return {
    id: `status-summary-${msg.turn_count}`,
    isUserMessage: false,
    isResponseLoading: false,
    isResponseRefining: isStreaming,
    messageContent: msg.content ?? '',
    role: 'assistant',
    bubble: false,
    showAvatar: false,
    floatAbove: false,
    statusDetails: details,
  };
};

const mapHistoryMessage = (
  msg: HistoryMessage,
  isLastTurn: boolean,
  isComplete: boolean,
  statusDetailsByTurn: Map<number, string>
): MessageType | null => {
  const { turn_count, role, space, content } = msg;

  if (
    !content ||
    space === SPACE_NAMES.PLACEHOLDER ||
    space === SPACE_NAMES.BUTTON ||
    space === SPACE_NAMES.BUTTONS ||
    space === SPACE_NAMES.PREVIEWS ||
    space === SPACE_NAMES.STATUS_DETAILS
  ) {
    return null;
  }
  if (space === SPACE_NAMES.STATUS_SUMMARY) {
    return buildStatusSummaryMessage(
      msg,
      isLastTurn,
      isComplete,
      statusDetailsByTurn
    );
  }

  if (space === SPACE_NAMES.ASSISTANT_MESSAGE) {
    return {
      id: `assistant-${turn_count}`,
      isUserMessage: false,
      isResponseLoading: false,
      messageContent: content,
      role: 'assistant',
      showFeedbackIcons: true,
    };
  }

  if (space === SPACE_NAMES.USER_MESSAGE || role === 'user') {
    return {
      id: `user-${turn_count}`,
      isUserMessage: true,
      isResponseLoading: false,
      messageContent: content,
      role: 'user',
    };
  }

  return null;
};

export const rehydrateMessages = (
  historyMessages: HistoryMessage[]
): MessageStreamYield => {
  const messages_normalized = historyMessages.map(normalizeHistoryMessage);

  let maxTurnCount = 0;
  let isComplete = false;
  const statusDetailsByTurn = new Map<number, string>();

  for (const msg of messages_normalized) {
    if (msg.turn_count > maxTurnCount) maxTurnCount = msg.turn_count;
    if (msg.turn_count === maxTurnCount && msg.done === true) isComplete = true;
    if (msg.space === SPACE_NAMES.STATUS_DETAILS && msg.content) {
      statusDetailsByTurn.set(
        msg.turn_count,
        (statusDetailsByTurn.get(msg.turn_count) ?? '') + msg.content
      );
    }
  }

  const messages: MessageType[] = [];
  let quickResponseData: QuickResponseData = {
    options: [],
    selectType: 'action',
  };
  let placeholder = DEFAULT_PLACEHOLDER;
  let previewData: PreviewItem[] = [];

  for (const msg of messages_normalized) {
    const isLastTurn = msg.turn_count === maxTurnCount;

    if (
      (msg.space === SPACE_NAMES.BUTTON || msg.space === SPACE_NAMES.BUTTONS) &&
      msg.content &&
      isLastTurn
    ) {
      quickResponseData = parseButtonSpace(
        msg.content as string | BackendButton[]
      );
      continue;
    }

    if (msg.space === SPACE_NAMES.PLACEHOLDER && msg.content) {
      placeholder = msg.content;
      continue;
    }

    if (msg.space === SPACE_NAMES.PREVIEWS && msg.content) {
      let items: PreviewItem[] = [];
      try {
        // TODO: remove with KZN-18583
        const parsed =
          typeof msg.content === 'string'
            ? JSON.parse(msg.content)
            : msg.content;
        items = Array.isArray(parsed) ? parsed : [];
      } catch {
        items = [];
      }
      if (items.length > 0) {
        messages.push({
          id: `previews-${msg.turn_count}`,
          isUserMessage: false,
          isResponseLoading: false,
          messageContent: '',
          role: 'assistant',
          showFeedbackIcons: false,
          previewData: items,
        });
      }
      if (isLastTurn) previewData = items;
      continue;
    }

    const message = mapHistoryMessage(
      msg,
      isLastTurn,
      isComplete,
      statusDetailsByTurn
    );
    if (message) messages.push(message);
  }

  return {
    messages,
    turnCount: maxTurnCount,
    isComplete,
    quickResponseData,
    placeholder,
    previewData,
  };
};
